/**
 * Confrontations directes (H2H) d'une paire — CONTEXTE D'AVANT-MATCH servi par
 * /data/h2h/<AAA>-<BBB>.json (fetch paresseux côté serveur + cache permanent).
 * Mis en cache côté client par paire ; un échec laisse la fiche sans le bloc
 * (jamais d'écran cassé, règle 8).
 */
export interface H2HMeeting {
  readonly date: string | null;
  readonly homeCode: string;
  readonly awayCode: string;
  readonly homeGoals: number;
  readonly awayGoals: number;
  readonly league: string | null;
}
interface H2HDoc {
  readonly pair: string;
  readonly meetings: H2HMeeting[];
}

const cache = new Map<string, H2HMeeting[] | null>();
const pending = new Map<string, Promise<H2HMeeting[] | null>>();

/** Clé canonique d'une paire (indépendante de l'ordre) — comme côté serveur. */
function pairKey(a: string, b: string): string {
  return [a, b].sort().join("-");
}

/** H2H d'une paire de codes FIFA (mis en cache par paire), ou null. */
export async function fetchH2H(codeA: string, codeB: string): Promise<H2HMeeting[] | null> {
  const k = pairKey(codeA, codeB);
  if (cache.has(k)) return cache.get(k) ?? null;
  const inflight = pending.get(k);
  if (inflight) return inflight;
  const p = fetch(`data/h2h/${k}.json`)
    .then((r) => (r.ok ? (r.json() as Promise<H2HDoc>) : null))
    .then((doc) => {
      const val = doc && Array.isArray(doc.meetings) ? doc.meetings : null;
      cache.set(k, val);
      return val;
    })
    .catch(() => null)
    .finally(() => {
      pending.delete(k);
    });
  pending.set(k, p);
  return p;
}

/** Bilan W/D/L vu de `code` sur une liste de confrontations. */
export function h2hRecord(meetings: readonly H2HMeeting[], code: string): { w: number; d: number; l: number } {
  let w = 0;
  let d = 0;
  let l = 0;
  for (const m of meetings) {
    const gf = m.homeCode === code ? m.homeGoals : m.awayGoals;
    const ga = m.homeCode === code ? m.awayGoals : m.homeGoals;
    if (gf > ga) w++;
    else if (gf < ga) l++;
    else d++;
  }
  return { w, d, l };
}
